import type { Breadcrumb, ErrorEvent } from '@sentry/core';

/**
 * Error reporting: what every process of this app shares, whichever Sentry SDK it runs.
 *
 * Errors go to GlitchTip at traces.dictaris.ro, which speaks Sentry's protocol, so the official
 * SDKs are used unchanged. One project per client, so a desktop crash and a browser one are
 * never counted as the same issue.
 *
 * A DSN is not a secret. It ships inside every copy of the app, as the API's address does, and
 * all it allows is sending an error in.
 *
 * Most of this file is about what must not be sent. These are reports from a medical product:
 * the text on screen is a patient's, and so, often, is what the app logs and requests. The SDKs'
 * defaults assume otherwise, so each default that could carry that text is switched off here,
 * and whatever still reaches an event is cut down in `scrubEvent` before it leaves.
 */

export const DESKTOP_DSN = 'https://de521d3267124a3abe66f7413f9c6efa@traces.dictaris.ro/1';
export const WEB_DSN = 'https://8c4895336bc742dd81cd2b277961cb69@traces.dictaris.ro/2';

/**
 * Integrations switched off by name, in every process that has them.
 *
 * - `BrowserSession`, `MainProcessSession`: release-health sessions, which GlitchTip does not
 *   accept. Every page view would be a request it throws away.
 * - `LocalVariables`: copies the variables of a failing main-process frame into the event. The
 *   PDF export's frame holds the whole report.
 * - `Screenshots`: a picture of the window, which is a patient's report more often than not. Off
 *   unless asked for already; named here so it cannot be switched on by accident.
 * - `SentryMinidump`: native crash dumps, which GlitchTip cannot read.
 * - `PreloadInjection`: the main process is bundled, so the preload it would inject is not on
 *   disk. The renderer reaches the main process over the SDK's own protocol instead.
 * - `Console`: the main process's console, which is whatever the app logs.
 */
const DROPPED_INTEGRATIONS = new Set([
  'BrowserSession',
  'MainProcessSession',
  'LocalVariables',
  'Screenshots',
  'SentryMinidump',
  'PreloadInjection',
  'Console',
]);

export function keepIntegration(integration: { name: string }): boolean {
  return !DROPPED_INTEGRATIONS.has(integration.name);
}

/**
 * A URL without its query, on both sides of a `#`.
 *
 * The web build and the desktop both route with a hash, and a route carries its own query — an
 * invitation's token, a search for a patient by name — so the part after the hash is cut at its
 * `?` as well. The path is what says where it happened.
 */
export function scrubUrl(url: string): string {
  const hashAt = url.indexOf('#');
  const address = hashAt >= 0 ? url.slice(0, hashAt) : url;
  const route = hashAt >= 0 ? url.slice(hashAt) : '';
  return withoutQuery(address) + withoutQuery(route);
}

function withoutQuery(part: string): string {
  const queryAt = part.indexOf('?');
  return queryAt >= 0 ? part.slice(0, queryAt) : part;
}

/**
 * Breadcrumbs keep the requests and navigations that led up to an error, without their queries,
 * and nothing that was printed or clicked.
 *
 * Console output is dropped because the desktop logs whole API responses while it works. Clicks
 * and typing are dropped because a clicked element is described by its label, and a label here
 * can be a patient's name. The renderers also turn both off at the source.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === 'console' || breadcrumb.category?.startsWith('ui.')) return null;

  const data = breadcrumb.data;
  if (data) {
    for (const key of ['url', 'from', 'to']) {
      if (typeof data[key] === 'string') data[key] = scrubUrl(data[key]);
    }
  }

  return breadcrumb;
}

/**
 * The last word on what leaves the machine.
 *
 * - The page address loses its query, and the request loses its body, cookies and every header
 *   except the browser's own name.
 * - Anything the SDK serialised from a thrown value that was not an Error goes. The services
 *   here throw axios responses when the API refuses, and an uncaught one arrives serialised —
 *   request body and `Authorization` header included. `reportCaughtError` never sends one; this
 *   is for the ones nobody caught.
 */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  const request = event.request;
  if (request) {
    if (request.url) request.url = scrubUrl(request.url);
    delete request.data;
    delete request.cookies;
    delete request.query_string;

    const userAgent = request.headers?.['User-Agent'];
    request.headers = userAgent ? { 'User-Agent': userAgent } : {};
  }

  if (event.extra) delete event.extra.__serialized__;

  return event;
}

/** Context attached to a caught error: only values that cannot carry a patient's text. */
export type ReportDetails = Record<string, string | number | boolean | null | undefined>;

interface RequestLike {
  status?: unknown;
  config?: { method?: unknown; url?: unknown };
  data?: { errors?: Array<{ id?: unknown }> };
}

/**
 * An axios error (`error.response`) or a thrown axios response, reduced to what is safe to send:
 * the method, the path without its query, the status, and the API's error id.
 */
export function describeRequest(error: unknown): ReportDetails | null {
  if (!error || typeof error !== 'object') return null;

  const failure = error as RequestLike & { response?: RequestLike; code?: unknown };
  const response = failure.response ?? (typeof failure.status === 'number' ? failure : undefined);
  const config = failure.config ?? response?.config;
  if (!config && !response) return null;

  const apiError = response?.data?.errors?.[0]?.id;

  return {
    method: typeof config?.method === 'string' ? config.method.toUpperCase() : undefined,
    url: typeof config?.url === 'string' ? scrubUrl(config.url) : undefined,
    status: typeof response?.status === 'number' ? response.status : undefined,
    code: typeof failure.code === 'string' ? failure.code : undefined,
    apiError: typeof apiError === 'string' ? apiError : undefined,
  };
}

/**
 * What `captureException` is given. An Error as it is; anything else replaced by an Error that
 * describes it, because the SDK serialises a thrown object whole — an axios response's request
 * body and `Authorization` header with it.
 */
export function asReportableError(error: unknown): Error {
  if (error instanceof Error) return error;

  const status = (error as RequestLike | null)?.status;
  const described =
    typeof error === 'string'
      ? error
      : typeof status === 'number'
        ? `The API answered ${status} with nothing to show`
        : `A ${error === null ? 'null' : typeof error} was thrown`;

  const replacement = new Error(described);
  replacement.name = typeof status === 'number' ? 'UnexplainedApiResponse' : 'NonErrorThrown';
  return replacement;
}
